import clsx from "clsx";
import { PREDICAMENT_STATE_YES } from "core/consts";
import CheckboxInputField, {
  isChecked,
} from "ds_legacy/components/CheckboxInputField";
import YesNoRadioInputField from "ds_legacy/components/YesNoRadioInputField";
import { dp } from "ds_legacy/materials/metrics";
import React, { MutableRefObject, useRef } from "react";
import { FormWatcher, useHideComponent } from "react-forms-state";

export type ActivableInputProps = {
  children: React.ReactNode;
  shouldDisableChange?: boolean;
  elementName: string;
  isFullWidth?: boolean;
  isImportant?: boolean;
  label: string;
  margin?: string;
  shouldMountChildren?: boolean;
  withSmallIndent?: boolean;
  shouldRenderYesNoRadioField?: boolean;
};

const setChildFieldsToNull = (
  ref: MutableRefObject<HTMLDivElement | null>,
  mutate: (value: any, name: string) => void,
) => {
  const formElement = ref.current;
  if (!formElement) return;

  [...formElement.querySelectorAll("input, select, textarea")].forEach(
    (element) => {
      const inputElement = element as HTMLInputElement;
      if (inputElement.name) {
        mutate(null, inputElement.name);
      }
    },
  );
};

export default function ActivableInput({
  children,
  elementName,
  isFullWidth = false,
  isImportant = false,
  label,
  margin,
  // form elements only receive their form state value if they are
  // mounted when the form is mounted (same render cycle)
  // to conditionally hide them while ensuring they exist in the DOM
  // we can resort to the display property for e.g.
  shouldDisableChange,
  shouldMountChildren,
  shouldRenderYesNoRadioField,
  withSmallIndent = false,
}: ActivableInputProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const hideComponent = useHideComponent(elementName);
  if (hideComponent) return null;

  if (shouldRenderYesNoRadioField)
    return (
      <div
        className={clsx("min-w-[300px]", {
          "w-auto": !isFullWidth,
          "w-full": isFullWidth,
        })}
        style={{ margin }}
      >
        <YesNoRadioInputField
          elementName={elementName}
          isImportant={isImportant}
          label={label}
          sideMutation={(value, mutate) => {
            if (!value) {
              setChildFieldsToNull(ref, mutate);
            }
          }}
        />
        <FormWatcher watchPath={elementName}>
          {({ watchedValue }) => (
            <div
              ref={ref}
            className={clsx({
              block: watchedValue === PREDICAMENT_STATE_YES,
              hidden: watchedValue !== PREDICAMENT_STATE_YES,
              "ml-2": withSmallIndent,
              "ml-9": !withSmallIndent,
            })}
            >
              {children}
            </div>
          )}
        </FormWatcher>
      </div>
    );

  return (
    <div
      className={clsx("min-w-[300px]", {
        "w-auto": !isFullWidth,
        "w-full": isFullWidth,
      })}
      style={{ margin }}
    >
      <CheckboxInputField
        elementName={elementName}
        isImportant={isImportant}
        label={label}
        maxWidth={dp(500)}
        shouldDisableChange={shouldDisableChange}
        sideMutation={(value, mutate) => {
          if (!value) {
            setChildFieldsToNull(ref, mutate);
          }
        }}
      />
      <FormWatcher watchPath={elementName}>
        {({ watchedValue }) => (
          <div
            ref={ref}
          className={clsx({
            block: isChecked(watchedValue),
            hidden: !isChecked(watchedValue),
            "ml-2": withSmallIndent,
            "ml-9": !withSmallIndent,
          })}
          >
            {isChecked(watchedValue) || shouldMountChildren ? children : null}
          </div>
        )}
      </FormWatcher>
    </div>
  );
}
